/* ═══════════════════════════════════════════════════════════════
   teamAssignable — ONE "who may I assign" filter (Decision 11), used
   by BOTH the Team widget's inline "+ Add person" row and TeamPanel's
   picker so the two surfaces never drift. Composes the pure org-chart
   mirror (shared/orgChart.ts) with the project-specific "not already
   assigned" / "active only" rules — neither of those belong in the
   shared helper, which knows nothing about a project.
   ═══════════════════════════════════════════════════════════════ */
import { assignableMembers } from '../../../../shared/orgChart';
import type { TeamMember } from '../../../../shared/entities';
import type { ProjectWidgetCtx } from './context';

/** Active members not already on the project that `ctx.authUser` may
 *  assign — mirrors the server's `projects.assign_anyone` /
 *  `projects.assign_team` enforcement (server.js:3694-3713) so the picker
 *  never offers a person the server will refuse. */
export function assignableTeamMembers(
  ctx: ProjectWidgetCtx,
  assignedIds: Set<string>
): TeamMember[] {
  // Org-chart walk needs the FULL member list (an inactive or
  // already-assigned manager can still sit in the middle of a reports_to
  // chain) — active/assigned filtering happens AFTER, same order as the
  // server (it walks team_members directly, unfiltered).
  const callerMemberId = ctx.authUser?.teamMemberId || ctx.authUser?.id || '';
  const allowed = assignableMembers(ctx.members, callerMemberId, {
    assignAnyone: ctx.can.assignAnyone,
    assignTeam: ctx.can.assignTeam,
  });
  return allowed.filter((m) => m.active && !assignedIds.has(m.id));
}
